import React, { useEffect, useState } from 'react';
import { 
  FileTextOutlined, 
  EyeOutlined, 
  RiseOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { apiClient } from '../lib/api';
import type { Section } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

interface DashboardStats {
  totalSections: number;
  publishedSections: number;
  totalImages: number;
  totalUsers: number;
  recentSections: Section[];
}

const StatCard: React.FC<{
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  trend?: string;
}> = ({ title, value, icon, color, trend }) => (
  <div className="admin-card">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-6 mb-1">{title}</p>
        <p className="text-2xl font-bold text-gray-9">{value}</p>
        {trend && (
          <p className="text-xs text-success flex items-center mt-1">
            <RiseOutlined className="mr-1" />
            {trend}
          </p>
        )}
      </div>
      <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center text-white text-xl`}>
        {icon}
      </div>
    </div>
  </div>
);

const RecentSectionCard: React.FC<{ section: Section }> = ({ section }) => (
  <div className="admin-card hover:shadow-md transition-shadow cursor-pointer">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <h3 className="font-medium text-gray-9 mb-1 line-clamp-1">{section.title}</h3>
        <p className="text-sm text-gray-6 mb-2 line-clamp-2">
          {section.shortDescription || section.longDescription}
        </p>
        <div className="flex items-center space-x-4 text-xs text-gray-5">
          <span className="flex items-center">
            <CalendarOutlined className="mr-1" />
            {new Date(section.updatedAt).toLocaleDateString()}
          </span>
          <span className="flex items-center">
            <EyeOutlined className="mr-1" />
            {section.viewCount} views
          </span>
          <span className={`px-2 py-1 rounded-full text-xs ${
            section.isPublished 
              ? 'bg-success-50 text-success-600' 
              : 'bg-gray-100 text-gray-600'
          }`}>
            {section.isPublished ? 'Published' : 'Draft'}
          </span>
        </div>
      </div>
      {section.featuredImage && (
        <img
          src={section.featuredImage.url}
          alt={section.featuredImage.alt}
          className="w-16 h-16 rounded-lg object-cover ml-3 flex-shrink-0"
        />
      )}
    </div>
  </div>
);

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch sections
        const sectionsResponse = await apiClient.getSections({ limit: 5 });
        const sections = sectionsResponse.data || [];

        // Fetch images count
        const imagesResponse = await apiClient.getImages({ limit: 1 });
        const totalImages = imagesResponse.pagination?.total || 0;

        // Fetch users (if admin)
        let totalUsers = 0;
        if (user?.role === 'admin') {
          try {
            const usersResponse = await apiClient.getUsers();
            totalUsers = usersResponse.data?.length || 0;
          } catch (error) {
            console.warn('Failed to fetch users:', error);
          }
        }

        const publishedSections = sections.filter(s => s.isPublished).length;

        setStats({
          totalSections: sectionsResponse.pagination?.total || sections.length,
          publishedSections,
          totalImages,
          totalUsers,
          recentSections: sections,
        });
      } catch (error: any) {
        console.error('Failed to fetch dashboard data:', error);
        setError(error.message || 'Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [user?.role]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-9">Dashboard</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="admin-card animate-pulse">
              <div className="flex items-center justify-between">
                <div>
                  <div className="h-4 bg-gray-3 rounded mb-2"></div>
                  <div className="h-8 bg-gray-3 rounded"></div>
                </div>
                <div className="w-12 h-12 bg-gray-3 rounded-lg"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-9">Dashboard</h1>
        <div className="admin-card text-center">
          <div className="text-error text-4xl mb-4">⚠️</div>
          <h2 className="text-lg font-medium text-gray-9 mb-2">Failed to Load Dashboard</h2>
          <p className="text-gray-6 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-9">Dashboard</h1>
          <p className="text-gray-6 mt-1">Welcome back, {user?.email}</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Sections"
          value={stats?.totalSections || 0}
          icon={<FileTextOutlined />}
          color="bg-primary-500"
        />
        <StatCard
          title="Published Sections"
          value={stats?.publishedSections || 0}
          icon={<EyeOutlined />}
          color="bg-success"
        />
        {/* <StatCard
          title="Total Images"
          value={stats?.totalImages || 0}
          icon={<PictureOutlined />}
          color="bg-warning"
        />
        {user?.role === 'admin' && (
          <StatCard
            title="Total Users"
            value={stats?.totalUsers || 0}
            icon={<TeamOutlined />}
            color="bg-info"
          />
        )} */}
      </div>

      {/* Recent Sections */}
      <div>
        <h2 className="text-lg font-semibold text-gray-9 mb-4">Recent Sections</h2>
        {stats?.recentSections && stats.recentSections.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {stats.recentSections.map((section) => (
              <RecentSectionCard key={section.id} section={section} />
            ))}
          </div>
        ) : (
          <div className="admin-card text-center py-12">
            <FileTextOutlined className="text-4xl text-gray-4 mb-4" />
            <h3 className="text-lg font-medium text-gray-7 mb-2">No Sections Yet</h3>
            <p className="text-gray-5 mb-4">Start creating content sections to see them here.</p>
            <button className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors">
              Create Section
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;